import { KernAlert, KernButton } from '@kern-ux-annex/kern-react-kit'
import { AnimatePresence } from 'motion/react'
import { useEffect, useId, useRef, useState } from 'react'

import { useFeatures, useMe, useSetFeature } from '../api/hooks'
import { useCurrentTenant } from '../app/tenant'
import { FeatureSwitch } from './FeatureSwitch'
import { ErrorAlert, useToast } from './feedback'

export const APP_VERSION = import.meta.env.VITE_APP_VERSION ?? '1.0.0'

const TAPS_TO_UNLOCK = 7
const TAP_WINDOW_MS = 4000

/**
 * The developer panel is hidden on purpose: it opens after seven quick taps on the version
 * number (as on Android). From the fourth tap a hint counts down.
 */
export function useSecretTaps(onUnlock: () => void) {
  const taps = useRef<number[]>([])
  const toast = useToast()
  return () => {
    const now = Date.now()
    taps.current = [...taps.current.filter((t) => now - t < TAP_WINDOW_MS), now]
    const left = TAPS_TO_UNLOCK - taps.current.length
    if (left <= 0) {
      taps.current = []
      onUnlock()
    } else if (left <= 3) {
      toast(left === 1 ? 'Noch 1 Klick bis zum Entwicklerpanel' : `Noch ${left} Klicks bis zum Entwicklerpanel`, 'info')
    }
  }
}

export function DevPanel({ open, onClose }: { open: boolean; onClose: () => void }) {
  const ref = useRef<HTMLDialogElement>(null)
  const titleId = useId()
  const { data: me } = useMe()
  const tenant = useCurrentTenant()
  const features = useFeatures(Boolean(tenant))
  const setFeature = useSetFeature()
  const toast = useToast()
  const [pending, setPending] = useState<string | null>(null)

  useEffect(() => {
    const dialog = ref.current
    if (!dialog) return
    if (open && !dialog.open) dialog.showModal()
    if (!open && dialog.open) dialog.close()
  }, [open])

  const canSwitch = Boolean(me?.platformAdmin && tenant)
  return (
    <dialog ref={ref} className="kern-dialog max-w-[44rem]" aria-labelledby={titleId} onClose={onClose}>
      <header className="kern-dialog__header">
        <h2 className="kern-title" id={titleId}>
          Entwicklerpanel
        </h2>
      </header>
      <section className="kern-dialog__body flex flex-col gap-16">
        <dl className="grid grid-cols-[auto_1fr] gap-x-16 gap-y-4 text-kern-16">
          <dt className="dms-muted">Version</dt>
          <dd>{APP_VERSION}</dd>
          <dt className="dms-muted">Kommune</dt>
          <dd>{tenant ? `${tenant.name} (${tenant.slug})` : '–'}</dd>
          <dt className="dms-muted">Angemeldet</dt>
          <dd>
            {me?.email ?? me?.name} {me?.platformAdmin ? '· Plattform-Administration' : me?.role ? `· ${me.role}` : ''}
          </dd>
        </dl>
        <div className="flex flex-col gap-8">
          <h3 className="kern-heading kern-heading-small">Funktionen (Feature-Toggles)</h3>
          <p className="kern-body kern-body--small dms-muted">
            Erweiterungen über den Basisumfang hinaus. Standardmäßig ausgeschaltet; Schalter gelten für die
            aktuelle Kommune.
          </p>
          {!canSwitch && (
            <KernAlert title="Nur Ansicht" variant="info">
              <span>Funktionen kann nur die Plattform-Administration umschalten.</span>
            </KernAlert>
          )}
          <ErrorAlert error={features.error} title="Funktionen konnten nicht geladen werden" />
          <ErrorAlert error={setFeature.error} title="Umschalten nicht möglich" />
          <ul className="flex flex-col gap-8">
            <AnimatePresence initial={false}>
              {(features.data ?? []).map((f) => (
                <FeatureSwitch
                  key={f.key}
                  feature={f}
                  disabled={!canSwitch}
                  busy={pending === f.key}
                  onToggle={async (enabled) => {
                    setPending(f.key)
                    try {
                      await setFeature.mutateAsync({ key: f.key, enabled })
                      toast(`${f.label}: ${enabled ? 'an' : 'aus'}`, 'info')
                    } catch {
                      // shown above via setFeature.error
                    } finally {
                      setPending(null)
                    }
                  }}
                />
              ))}
            </AnimatePresence>
          </ul>
        </div>
      </section>
      <footer className="kern-dialog__footer">
        <KernButton variant="secondary" label="Schließen" onClick={onClose} />
      </footer>
    </dialog>
  )
}
